import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/24/outline';

interface GalleryPreviewProps {
  visibleElements: Set<string>;
  galleryRef: React.RefObject<HTMLDivElement | null>;
}

export default function GalleryPreview({ visibleElements, galleryRef }: GalleryPreviewProps) {
  // State for gallery images
  const [galleryImages, setGalleryImages] = useState<string[]>([]);

  // Fetch gallery images from /data/gallery/gallery.json
  useEffect(() => {
    fetch('/data/gallery/gallery.json')
      .then((res) => res.json())
      .then((data) => {
        if (data.images && Array.isArray(data.images)) {
          setGalleryImages(data.images);
        }
      })
      .catch((error) => {
        console.error('Failed to fetch gallery images:', error);
      });
  }, []);

  return (
    <div 
      ref={galleryRef}
      data-animate="gallery"
      className="bg-white py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className={`mx-auto max-w-2xl text-center transition-all duration-1000 ease-out transform ${
          visibleElements.has('gallery')
            ? 'opacity-100 translate-y-0 scale-100'
            : 'opacity-0 translate-y-16 scale-95'
        }`}>
          <h2 className={`text-base font-semibold leading-7 text-primary-600 transition-all duration-1000 delay-200 ease-out ${
            visibleElements.has('gallery')
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-8'
          }`}>Gallery</h2>
          <p className={`mt-2 text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl transition-all duration-1000 delay-300 ease-out ${
            visibleElements.has('gallery')
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-8'
          }`}>
            Glimpses of Paradise
          </p>
          <p className={`mt-6 text-lg leading-8 text-neutral-600 transition-all duration-1000 delay-400 ease-out ${
            visibleElements.has('gallery')
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-8'
          }`}>
            Experience the beauty of our homestay and the surrounding landscapes through our photo gallery
          </p>
        </div>

        {/* Gallery Grid */}
        <div className={`w-full mt-16 transition-all duration-1000 delay-600 ease-out transform ${
          visibleElements.has('gallery')
            ? 'opacity-100 translate-y-0 scale-100'
            : 'opacity-0 translate-y-20 scale-95'
        }`}>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {galleryImages.slice(0, 8).map((imageUrl, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-2xl bg-neutral-100 aspect-square cursor-pointer transform transition-all duration-500 hover:scale-105 hover:shadow-xl"
              >
                <img
                  src={imageUrl}
                  alt={`Gallery ${index + 1}`}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                  onError={(e) => {
                    // Fallback to placeholder if image fails to load
                    e.currentTarget.src = 'https://images.unsplash.com/photo-1544735716-392fe2489ffa?q=80&w=500&auto=format&fit=crop&ixlib=rb-4.0.3';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300" />
              </div>
            ))}
          </div>
        </div>

        {/* View More Button */}
        <div className="mt-16 text-center">
          <Link
            to="/gallery"
            className="inline-flex items-center gap-x-2 rounded-lg bg-primary-600 px-6 py-3 text-base font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 transition-all duration-200 hover:shadow-lg"
          >
            View Full Gallery
            <ArrowRightIcon className="h-5 w-5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
